import './index.css'
import cn from 'clsx'
import captureError from '@disco/utils/captureError'

import React, { useEffect, useRef, useState } from 'react'
import ReactDOM from 'react-dom'
import { useDispatch } from 'hooks/use-redux'
import type { PrePopulatedSubscriptionData } from 'types/business'
import type { AccountPlan } from 'types/business2020'
import type { ProductData, Summary } from 'types/summary'
import type { User } from 'types/user'
import type { PricingFormState } from '@disco/state-management/reducers/forms/pricing'
import BillingInformation from 'components/billing-information'
import ChargebeeLoader from './chargebee-loader'
import Row from 'components/common/row'
import Alert from 'components/ui/alert'
import Button from 'components/ui/button'
import ButtonContainer from 'components/ui/button-container'
import ContactUsLink from 'components/ui/contact-us-link'
import Header from 'components/ui/heading'
import Label from 'components/ui/label'
import ModalWindow from 'components/ui/modal-window'
import Stack from 'components/ui/stack'
import Tag from 'components/ui/tag'
import Text from 'components/ui/text'
import TextField from 'components/ui/text-field'
import BillingSummary from './billing-summary'
import ChargebeeFormErrModal from './error-modal'
import { SubscriptionDisclaimerText } from 'views/pages/settings/billing/SubscriptionDisclaimerText'
import { Box } from 'components/ds'
import { ChargebeeFormInputs } from './ChargebeeFormInputs'
import type { ChargebeeComponent } from './ChargebeeFormInputs'

export function getFormValue(form, filterTag) {
  const result = {}
  ;[...form.elements]
    .filter(({ tagName, type }) => {
      return tagName === filterTag && type !== 'hidden'
    })
    .forEach(({ name, value }) => {
      result[name] = value
    })
  return result
}

type Props = {
  createSubscription: (arg0: Record<string, any>) => Promise<any>
  onRequestCloseUpdateModal: (...args: Array<any>) => any
  productData: ProductData
  applyCouponCode: (...args: Array<any>) => any
  setUpdateModal: (...args: Array<any>) => any
  summary: Summary & {
    loading: boolean
    loaded: boolean
    error: null | { error_code: string; error_msg: string }
    coupon_code: string | null | undefined
    signupRequest: any
  }
  uploadPlan: (...args: Array<any>) => any
  user: User
  tracksQuantity: number
  usersQuantity: number
  period: 'monthly' | 'annual'
  billingRef: {
    current: HTMLElement | null | undefined
  }
  updatePricingForm: (...args: Array<any>) => any
  plan: AccountPlan
  pricingForm: PricingFormState
  isOverQuota: boolean
  setPaymentDetails: (...args: Array<any>) => any

  /**
   * Change the layout for mobile
   */
  isPhone: boolean

  /**
   * Show all or none mobile content
   */
  showAllMobileContent: boolean
  prePopulatedSubscriptionData: PrePopulatedSubscriptionData
}

export const ChargebeeForm = ({
  createSubscription,
  onRequestCloseUpdateModal,
  productData,
  applyCouponCode,
  setUpdateModal,
  summary,
  uploadPlan,
  user,
  tracksQuantity,
  usersQuantity,
  period,
  billingRef,
  updatePricingForm,
  plan,
  pricingForm,
  isOverQuota,
  setPaymentDetails,
  isPhone,
  showAllMobileContent,
  prePopulatedSubscriptionData,
}: Props): React.ReactElement<'form'> => {
  const dispatch = useDispatch()
  const chargebeeForm = useRef(null)
  const [couponCode, setCouponCode] = useState('')
  const [couponCodeErr, setCouponCodeErr] = useState<string | null>(null)
  const [sendFormWaiting, setSendFormWaiting] = useState(false)
  const [loadingChargebeeToken, setLoadingChargebeeToken] = useState(false)
  const [couponNotApply, setCouponNotApply] = useState(false)
  const [paymentDetailsSaved, setPaymentDetailsSaved] = useState(false)
  const [billingInformation, setBillingInformation] = useState({
    form: {
      address: null,
      city: null,
      state: null,
      zip: null,
      country: null,
    },

    confirm() {},
  })
  const [token, setToken] = useState('')
  const [chargebeeLoaded, setChargebeeLoaded] = useState(false)
  const [chargebeeError, setChargebeeError] = useState(false)
  // Chargebee throws an error with useState when setting the tokenize ref
  const chargebeeComponentRef = useRef<ChargebeeComponent | null>(null)
  const ModalButtonContainer = isPhone ? React.Fragment : ButtonContainer
  const modalButtonContainerProps = isPhone
    ? {}
    : {
        alignRight: true,
      }
  const successModal = (
    <ModalWindow
      header={<Header size={700}>Subscription confirmed!</Header>}
      onClose={onRequestCloseUpdateModal}
      isOpen
      hiddenCloseButton
      width="small"
    >
      <Stack space="small">
        <Text type="paragraph">Thank you! You’re now subscribed to DISCO.</Text>

        <ModalButtonContainer {...modalButtonContainerProps}>
          <Button
            caption="Get started"
            appearance="primary"
            onClick={onRequestCloseUpdateModal}
            fullWidth={isPhone}
            textCenter={isPhone}
          />
        </ModalButtonContainer>
      </Stack>
    </ModalWindow>
  )
  useEffect(() => {
    const { coupon_code, total_discount_in_cents, line_items, error } = summary
    const couponCodeValid = line_items?.find(({ kind }) => kind === 'coupon')
    setCouponNotApply(false)

    if (coupon_code && total_discount_in_cents === 0 && couponCodeValid) {
      // TODO: remove as seems impossible case, because Chargebee returns {"error_code": "coupon_not_applicable"}
      setCouponNotApply(true)
      return
    }

    if (coupon_code && error?.error_code === 'coupon_not_applicable') {
      setCouponNotApply(true)
      return
    }

    if (coupon_code && !total_discount_in_cents) {
      setCouponCodeErr('This field is not valid. Please update it.')
    }
  }, [summary])
  useEffect(() => {
    setCouponCodeErr(null)
  }, [period])

  const getPaymentDetailsWithNewToken = newToken => {
    const {
      id: userId,
      business: { id: businessId },
    } = user
    const formValue: {
      first_name?: string
      last_name?: string
    } = getFormValue(chargebeeForm.current, 'INPUT')

    const billing_address = {
      line1: billingInformation.form.address,
      city: billingInformation.form.city,
      state: billingInformation.form.state,
      zip: billingInformation.form.zip,
      country: billingInformation.form.country,
      first_name: formValue.first_name,
      last_name: formValue.last_name,
      // reference: `${businessId}-${userId}`,
    }
    return {
      card_token: newToken.token,
      billing_address,
      testing_card: null,
    }
  }

  const handleSubmit = async e => {
    e.preventDefault()

    if (!chargebeeComponentRef.current?.tokenize) {
      return
    }

    setSendFormWaiting(true)
    setLoadingChargebeeToken(true)

    const formValue: {
      first_name?: string
      last_name?: string
    } = getFormValue(chargebeeForm.current, 'INPUT')

    try {
      const newToken = await chargebeeComponentRef.current.tokenize({
        firstName: formValue.first_name,
        lastName: formValue.last_name,
      })
      setToken(newToken)

      const paymentDetails = getPaymentDetailsWithNewToken(newToken)
      await createSubscription({
        subscription: {
          ...summary.signupRequest,
          ...paymentDetails,
        },
      })
      setUpdateModal(successModal)
      uploadPlan()
    } catch (error) {
      setSendFormWaiting(false)
      setLoadingChargebeeToken(false)

      setUpdateModal(
        <ChargebeeFormErrModal
          error={error}
          onRequestClose={onRequestCloseUpdateModal}
        />,
      )
      captureError(error, {
        description: 'chargebee.token() error inside handleSubmit()',
      })
    }
  }

  const handleApplyCode = () => {
    if (couponCode && summary.coupon_code) {
      return setCouponCodeErr('You can only use one coupon at the time.')
    }

    setCouponNotApply(false)

    if (!couponCode) {
      setCouponCodeErr('This field is not valid. Please update it.')
    } else if (couponCodeErr) {
      setCouponCodeErr(null)
    }

    if (couponCode) {
      applyCouponCode(couponCode)
    }
  }

  const handleRemoveCoupon = () => {
    if (couponCode) {
      applyCouponCode(null)
      setCouponCodeErr(null)
    }
  }

  const onBillingInformationChange = ({ form, confirm }) => {
    setPaymentDetailsSaved(false)
    dispatch(
      updatePricingForm({
        paymentDetailsSaved: false,
      }),
    )
    setBillingInformation({
      form,
      confirm,
    })
  }

  const handleConfirmDetails = async e => {
    e.preventDefault()
    billingInformation.confirm()

    if (!chargebeeComponentRef.current?.tokenize) {
      return
    }

    setLoadingChargebeeToken(true)
    setPaymentDetailsSaved(false)
    setPaymentDetails(null)

    try {
      const formValue: {
        first_name?: string
        last_name?: string
      } = getFormValue(chargebeeForm.current, 'INPUT')

      // if (true) return
      const newToken = await chargebeeComponentRef.current.tokenize({
        firstName: formValue.first_name,
        lastName: formValue.last_name,
      })

      setToken(newToken)
      const paymentDetails = getPaymentDetailsWithNewToken(newToken)
      setPaymentDetails(paymentDetails)
      setLoadingChargebeeToken(false)
      setPaymentDetailsSaved(true)
      dispatch(
        updatePricingForm({
          paymentDetailsSaved: true,
        }),
      )
    } catch (error) {
      setLoadingChargebeeToken(false)
      setUpdateModal(
        <ChargebeeFormErrModal
          error={error}
          onRequestClose={onRequestCloseUpdateModal}
        />,
      )
      captureError(error, {
        description: 'chargebee.token() error inside handleConfirmDetails()',
      })
    }
  }

  const { total_in_cents, loaded: loadedSummary } = summary
  const hideMobileContent = isPhone && !showAllMobileContent

  const onInvalid = event => {
    event.preventDefault()
  }

  const onChargebeeLoad = () => {
    setChargebeeLoaded(true)
    setChargebeeError(false)
  }

  const onChargebeeError = () => {
    setChargebeeError(true)
    setPaymentDetailsSaved(false)
    setPaymentDetails(null)
    setToken('')
  }

  const chargebeeLoader = (
    <ChargebeeLoader onLoad={onChargebeeLoad} onError={onChargebeeError} />
  )
  return (
    <form
      className="ChargebeeForm"
      onSubmit={handleConfirmDetails}
      ref={chargebeeForm}
    >
      <div id="chargebee-form">
        <section
          className={cn('ChargebeeForm_Section', {
            'ChargebeeForm_Section--Hide': hideMobileContent,
          })}
        >
          <h2 className="ChargebeeForm_Title">Customer Information</h2>
          <Row columnsSize={!isPhone ? [6, 6] : [12, 12]}>
            <div>
              <TextField
                required
                name="first_name"
                label="First name"
                onInvalid={onInvalid}
                patternErrorMessage="This field is required"
              />
            </div>
            <div>
              <TextField
                required
                name="last_name"
                label="Last name"
                onInvalid={onInvalid}
                patternErrorMessage="This field is required"
              />
            </div>
          </Row>

          <div className="ChargebeeForm_Line" />

          <h2 className="ChargebeeForm_Title ChargebeeForm_Title--WithSubTitle">
            Billing Information
          </h2>
          <h3 className="ChargebeeForm_SubTitle">
            All transactions are secure and encrypted.
          </h3>

          <BillingInformation onChange={onBillingInformationChange} />

          <div className="ChargebeeForm_Line" />

          <h2 className="ChargebeeForm_Title ChargebeeForm_Title--WithSubTitle">
            Payment information
          </h2>
          <Stack>
            {chargebeeLoaded ? (
              chargebeeLoader
            ) : (
              <Row columnsSize={[12]}>
                <div>{chargebeeLoader}</div>
              </Row>
            )}

            <div
              className={cn({
                'ChargebeeForm_Section--Hide':
                  !chargebeeLoaded || chargebeeError,
              })}
            >
              <h3 className="ChargebeeForm_SubTitle">
                All transactions are secure and encrypted.
              </h3>

              {chargebeeLoaded && (
                <ChargebeeFormInputs
                  chargebeeLoaded={chargebeeLoaded}
                  onReady={chargebeeComponent =>
                    (chargebeeComponentRef.current = chargebeeComponent)
                  }
                />
              )}

              <Row
                columnsSize={!isPhone ? [4, 4, 4] : [12, 12, 12]}
                marginBottom={false}
              >
                <div>
                  <TextField
                    required
                    name="first_name"
                    label="First name"
                    onInvalid={onInvalid}
                    patternErrorMessage="This field is required"
                    autoComplete="cc-given-name"
                  />
                </div>
                <div>
                  <TextField
                    required
                    name="last_name"
                    label="Last name"
                    onInvalid={onInvalid}
                    patternErrorMessage="This field is required"
                    autoComplete="cc-family-name"
                  />
                </div>
                <div>
                  <TextField
                    required
                    name="zip"
                    label="Billing ZIP/postal code"
                    onInvalid={onInvalid}
                    patternErrorMessage="This field is required"
                    autoComplete="card postal-code"
                  />
                </div>
              </Row>
              <Box mb="5">
                <SubscriptionDisclaimerText subscriptionPeriod={period} />
              </Box>
              <Row columnsSize={[12]}>
                <div>
                  <Button
                    appearance="primary"
                    caption="Confirm details"
                    progressIndicator={!loadedSummary || loadingChargebeeToken}
                    progressCaption="Loading"
                    type="submit"
                  />
                </div>
              </Row>
            </div>
          </Stack>
        </section>
        <section
          className={cn('ChargebeeForm_Section', {
            'ChargebeeForm_Section--Hide': hideMobileContent,
          })}
        >
          <h2 className="ChargebeeForm_Title">Coupon</h2>
          {!prePopulatedSubscriptionData.coupon_code && (
            <>
              <Label caption="Coupon code" />

              <ButtonContainer>
                <div>
                  <TextField
                    error={couponCodeErr}
                    name="coupon_code"
                    placeholder="Insert coupon code"
                    value={couponCode}
                    onChange={event => {
                      setCouponCode(event.target.value)
                    }}
                  />
                </div>
                <div>
                  <Button
                    appearance="primary"
                    caption={loadedSummary ? 'Apply Code' : 'Loading...'}
                    disabled={
                      !loadedSummary ||
                      (couponCode && couponCode === summary.coupon_code) ||
                      !couponCode
                    }
                    onClick={handleApplyCode}
                  />
                </div>
              </ButtonContainer>
            </>
          )}

          <div className="ChargebeeForm_CouponCodeWrapper">
            {couponNotApply && (
              <div className="ChargebeeForm_CouponCodeAlert">
                <Alert status="warning" icon="warning">
                  The coupon code doesn’t apply to the plan you’ve selected.
                  Please check the code and{' '}
                  <ContactUsLink intercomMsg="I have a question about a coupon">
                    contact us
                  </ContactUsLink>{' '}
                  with any questions.
                </Alert>
              </div>
            )}
            {!Boolean(summary.coupon_code) ? null : (
              <div className="ChargebeeForm_CouponCodeTag">
                <Tag
                  tag={summary.coupon_code || ''}
                  onRemoveClick={
                    !prePopulatedSubscriptionData.coupon_code
                      ? handleRemoveCoupon
                      : undefined
                  }
                />
              </div>
            )}
          </div>
        </section>
        {billingRef.current instanceof HTMLElement &&
          ReactDOM.createPortal(
            <BillingSummary
              period={period}
              updatePricingForm={updatePricingForm}
              loadedSummary={loadedSummary}
              plan={plan}
              usersQuantity={usersQuantity}
              tracksQuantity={tracksQuantity}
              summary={summary}
              pricingForm={pricingForm}
              handleRemoveCoupon={handleRemoveCoupon}
              total_in_cents={total_in_cents}
              sendFormWaiting={sendFormWaiting}
              handleSubmit={handleSubmit}
              isOverQuota={isOverQuota}
              paymentDetailsSaved={paymentDetailsSaved}
              isPhone={isPhone}
              prePopulatedSubscriptionData={prePopulatedSubscriptionData}
            />,
            billingRef.current,
          )}
      </div>
      <input id="host-token" type="hidden" disabled value={token} />
    </form>
  )
}
